import { useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { X, Upload, Star, StarOff, Image as ImageIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

export interface ImageFile {
  id: string;
  file?: File;
  url: string;
  isFeatured: boolean;
  isExisting?: boolean;
}

interface PropertyImageUploadProps {
  value?: ImageFile[];
  onChange: (images: ImageFile[]) => void;
  maxImages?: number;
  className?: string;
}

export function PropertyImageUpload({
  value = [],
  onChange,
  maxImages = 10,
  className
}: PropertyImageUploadProps) {
  const [images, setImages] = useState<ImageFile[]>(value);

  // Sync with external value changes
  useEffect(() => {
    setImages(value);
  }, [value]);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const remainingSlots = maxImages - images.length;
    const filesToProcess = acceptedFiles.slice(0, remainingSlots);

    if (acceptedFiles.length > remainingSlots) {
      toast.warning(`Máximo de ${maxImages} imagens permitidas. ${acceptedFiles.length - remainingSlots} imagens foram ignoradas.`);
    }

    const newImages: ImageFile[] = filesToProcess.map((file, index) => ({
      id: `new-${Date.now()}-${index}`,
      file,
      url: URL.createObjectURL(file),
      isFeatured: images.length === 0 && index === 0, // First image is featured if no images exist
      isExisting: false
    }));

    const updatedImages = [...images, ...newImages];
    setImages(updatedImages);
    onChange(updatedImages);
  }, [images, maxImages, onChange]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.webp']
    },
    maxSize: 5 * 1024 * 1024, // 5MB
    disabled: images.length >= maxImages
  });

  const removeImage = (imageId: string) => {
    const imageToRemove = images.find(img => img.id === imageId);
    
    // Clean up object URL if it's a new file
    if (imageToRemove && imageToRemove.file) {
      URL.revokeObjectURL(imageToRemove.url);
    }

    const updatedImages = images.filter(img => img.id !== imageId);
    
    // If we removed the featured image, make the first remaining image featured
    if (imageToRemove?.isFeatured && updatedImages.length > 0) {
      updatedImages[0].isFeatured = true;
    }

    setImages(updatedImages);
    onChange(updatedImages);
  };

  const setFeaturedImage = (imageId: string) => {
    const updatedImages = images.map(img => ({
      ...img,
      isFeatured: img.id === imageId
    }));

    setImages(updatedImages);
    onChange(updatedImages);
  };

  const reorderImages = (dragIndex: number, hoverIndex: number) => {
    const draggedImage = images[dragIndex];
    const updatedImages = [...images];
    updatedImages.splice(dragIndex, 1);
    updatedImages.splice(hoverIndex, 0, draggedImage);

    setImages(updatedImages);
    onChange(updatedImages);
  };

  return (
    <div className={cn("space-y-4", className)}>
      {/* Upload Area */}
      {images.length < maxImages && (
        <div
          {...getRootProps()}
          className={cn(
            "border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors",
            isDragActive
              ? "border-primary bg-primary/5"
              : "border-muted-foreground/25 hover:border-primary/50 hover:bg-muted/50"
          )}
        >
          <input {...getInputProps()} />
          <div className="flex flex-col items-center gap-2">
            <Upload className="h-8 w-8 text-muted-foreground" />
            <div className="text-sm">
              {isDragActive ? (
                <p>Solte as imagens aqui...</p>
              ) : (
                <div>
                  <p className="font-medium">Clique ou arraste imagens para cá</p>
                  <p className="text-muted-foreground">
                    Máximo {maxImages} imagens • JPG, PNG, WebP • Máx. 5MB cada
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Images Grid */}
      {images.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium">
              Imagens ({images.length}/{maxImages})
            </h3>
            <p className="text-xs text-muted-foreground">
              Clique na estrela para definir como imagem principal
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {images.map((image, index) => (
              <Card key={image.id} className="relative group overflow-hidden">
                <CardContent className="p-0">
                  <div className="aspect-square relative">
                    <img
                      src={image.url}
                      alt={`Imagem ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                    
                    {/* Featured Badge */}
                    {image.isFeatured && (
                      <Badge 
                        className="absolute top-2 left-2 bg-primary text-primary-foreground"
                      >
                        <Star className="h-3 w-3 mr-1 fill-current" />
                        Principal
                      </Badge>
                    )}

                    {/* Existing Image Badge */}
                    {image.isExisting && (
                      <Badge 
                        variant="secondary"
                        className="absolute top-2 right-2"
                      >
                        <ImageIcon className="h-3 w-3 mr-1" />
                        Existente
                      </Badge>
                    )}

                    {/* Overlay with actions */}
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                      <Button
                        size="sm"
                        variant={image.isFeatured ? "default" : "secondary"}
                        onClick={() => setFeaturedImage(image.id)}
                        className="h-8 w-8 p-0"
                      >
                        {image.isFeatured ? (
                          <Star className="h-4 w-4 fill-current" />
                        ) : (
                          <StarOff className="h-4 w-4" />
                        )}
                      </Button>
                      
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => removeImage(image.id)}
                        className="h-8 w-8 p-0"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Help Text */}
      {images.length === 0 && (
        <div className="text-center py-8">
          <ImageIcon className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-sm text-muted-foreground">
            Nenhuma imagem adicionada ainda
          </p>
        </div>
      )}
    </div>
  );
}